import { useOpen } from "@/app/hooks";
import { UpdateFilters } from "@/app/hooks/useFilters";
import {
  DidoxDocument,
  GetDidoxDocsParams,
  GetDidoxDocuments,
} from "@/entities/didox/types";
import { UIPagination } from "@/shared/ui/ui-pagination";
import {
  Button,
  Fade,
  Menu,
  Stack,
  IconButton,
  Typography,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import TrashIcon from "@mui/icons-material/Delete";

interface Props {
  documents?: GetDidoxDocuments;
  filters: GetDidoxDocsParams;
  updateFilters: UpdateFilters<GetDidoxDocsParams>;
  selected: DidoxDocument[];
  setSelected: React.Dispatch<React.SetStateAction<DidoxDocument[]>>;
  onSubmit: () => void;
}

export const DidoxDocumentsModalActions: FC<Props> = ({
  documents,
  filters,
  updateFilters,
  selected,
  onSubmit,
  setSelected,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { open, setOpen } = useOpen();

  const handleClick = () => {
    setOpen(!open);
  };

  const id = open ? "didox-documents-modal-selected-docs" : undefined;

  const handleDeleteDocument = (doc: DidoxDocument) => {
    setSelected((prev) => prev.filter((d) => d.doc_id !== doc.doc_id));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={2}>
        <UIPagination
          count={Math.ceil((documents?.total ?? 0) / (filters?.limit || 1))}
          page={filters.page}
          onChange={(_, page) => updateFilters({ page })}
        />

        <Fade in={!!selected.length}>
          <Button
            size="small"
            variant="outlined"
            color="info"
            type="button"
            ref={setAnchorEl}
            aria-describedby={id}
            onClick={handleClick}
          >
            {t("selected")}: {selected.length}
          </Button>
        </Fade>
      </Stack>

      <Fade in={!!selected.length}>
        <Button onClick={onSubmit}>{t("select")}</Button>
      </Fade>

      {/* Меню с выбранными документами, с возможностью удалять из выбранного списка */}
      <Menu
        id={id}
        anchorEl={anchorEl}
        open={open && !!selected.length}
        onClose={handleClick}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 480,
              width: "100%",
            },
          },
        }}
      >
        {selected.map((doc) => (
          <MenuItem disableRipple key={doc.doc_id}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              width="100%"
            >
              <Typography variant="body2">{doc.name}</Typography>
              <Tooltip title={t("remove_from_list")}>
                <IconButton onClick={() => handleDeleteDocument(doc)}>
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
